import React, { useState, useEffect } from "react";
import ComponentCard from "../../../components/common/ComponentCard";
import StatCard from "../../../components/common/StatCard";
import BarChartOne from "../../../components/charts/bar/BarChartOne";
import LineChartOne from "../../../components/charts/line/LineChartOne";
import useApi, { VentasReservasStats } from "../../../hooks/useApi";
import { DollarSign, Users, Tag, AlertCircle, Filter, Calendar, Award } from "react-feather";
import { format, subMonths } from "date-fns";
import { toast } from "react-hot-toast";

export default function VentasReservasView() {
  const [stats, setStats] = useState<VentasReservasStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: format(subMonths(new Date(), 12), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd')
  });
  const { formatCurrency, getVentasReservasStats } = useApi();

  // Fetch data from the API
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      
      try {
        // Get data from the API
        const data = await getVentasReservasStats(
          dateRange.startDate,
          dateRange.endDate
        );
        
        // Round all numeric values to nearest integer
        data.ingresosTotales = Math.round(data.ingresosTotales);
        data.numeroReservas = Math.round(data.numeroReservas);
        data.ticketPromedio = Math.round(data.ticketPromedio);
        data.tasaCancelacion = Math.round(data.tasaCancelacion);
        data.tendencia = Math.round(data.tendencia);
        data.reservasTipoCliente.b2c = Math.round(data.reservasTipoCliente.b2c);
        data.reservasTipoCliente.b2b = Math.round(data.reservasTipoCliente.b2b);
        data.reservasPorMes = data.reservasPorMes.map(v => Math.round(v));
        data.ingresosPorMes = data.ingresosPorMes.map(v => Math.round(v));
        
        setStats(data);
      } catch (error) {
        console.error("Error fetching stats:", error);
        toast.error("Error al cargar estadísticas");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [dateRange, getVentasReservasStats]);

  // Show skeleton UI when loading or when there's an error (stats is null)
  if (isLoading || !stats) {
    return (
      <div className="space-y-8">
        {/* Skeleton for key metrics */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
              <div className="animate-pulse">
                <div className="flex items-center justify-between">
                  <div className="h-4 w-1/3 rounded bg-gray-200 dark:bg-gray-700"></div>
                  <div className="h-3 w-10 rounded bg-gray-200 dark:bg-gray-700"></div>
                </div>
                <div className="mt-3 h-6 w-2/3 rounded bg-gray-200 dark:bg-gray-700"></div>
                <div className="mt-2 h-3 w-1/2 rounded bg-gray-200 dark:bg-gray-700"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Skeleton for charts and detailed stats */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800 lg:col-span-1">
            <div className="animate-pulse">
              <div className="h-5 w-1/2 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div className="mt-4 space-y-3">
                <div className="h-4 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
                <div className="h-4 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
                <div className="h-4 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800 lg:col-span-2">
            <div className="animate-pulse">
              <div className="h-5 w-1/3 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="h-24 rounded bg-gray-200 dark:bg-gray-700"></div>
                <div className="h-24 rounded bg-gray-200 dark:bg-gray-700"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Skeleton for graphs */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
              <div className="animate-pulse">
                <div className="h-5 w-1/3 rounded bg-gray-200 dark:bg-gray-700"></div>
                <div className="mt-2 h-3 w-1/4 rounded bg-gray-200 dark:bg-gray-700"></div>
                <div className="mt-4 h-64 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const barChartData = [
    {
      name: "Número de reservas",
      data: stats.reservasPorMes,
    },
  ];

  const lineChartData = [
    {
      name: "Ingresos mensuales",
      data: stats.ingresosPorMes,
    },
  ];

  // Function to handle date range changes (for future use)
  const handleDateRangeChange = (start: string, end: string) => {
    setDateRange({
      startDate: start,
      endDate: end
    });
  };

  return (
    <div className="space-y-8">
      {/* Métricas clave */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
          <StatCard
            icon={<DollarSign className="h-5 w-5" />}
            title="Ingresos totales"
            value={formatCurrency(stats.ingresosTotales)}
            trend={stats.tendencia}
            loading={false}
            description="Suma de todas las reservas cobradas"
          />
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
          <StatCard
            icon={<Users className="h-5 w-5" />}
            title="Número de reservas"
            value={stats.numeroReservas.toString()}
            trend={stats.tendencia || 0}
            loading={false}
            description="Cantidad total de reservas procesadas"
          />
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
          <StatCard
            icon={<Tag className="h-5 w-5" />}
            title="Ticket promedio"
            value={formatCurrency(stats.ticketPromedio)}
            trend={stats.tendencia || 0}
            loading={false}
            description="Ingresos totales ÷ Número de reservas"
          />
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
          <StatCard
            icon={<AlertCircle className="h-5 w-5" />}
            title="Tasa de cancelación"
            value={`${stats.tasaCancelacion}%`}
            trend={-(stats.tendencia || 0)}
            loading={false}
            description="(Reservas canceladas ÷ Reservas totales) × 100%"
          />
        </div>
      </div>

      {/* Gráficos y estadísticas detalladas */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Reservas B2C vs B2B */}
        <ComponentCard
          title="Reservas por tipo de cliente"
          className="lg:col-span-1"
        >
          {stats.numeroReservas > 0 ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-blue-500"></div>
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    B2C (Clientes directos)
                  </span>
                </div>
                <span className="text-lg font-medium text-gray-900 dark:text-white">
                  {stats.reservasTipoCliente.b2c}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-green-500"></div>
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    B2B (Agencias)
                  </span>
                </div>
                <span className="text-lg font-medium text-gray-900 dark:text-white">
                  {stats.reservasTipoCliente.b2b}
                </span>
              </div>

              <div className="mt-4 h-4 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                <div 
                  className="h-full rounded-full bg-blue-500"
                  style={{ 
                    width: `${Math.round((stats.reservasTipoCliente.b2c / stats.numeroReservas) * 100)}%` 
                  }}
                ></div>
              </div>

              <p className="text-xs text-gray-500 dark:text-gray-400">
                {Math.round((stats.reservasTipoCliente.b2c / stats.numeroReservas) * 100)}% de reservas son de clientes directos
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <div className="mb-3 h-12 w-12 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                <Users className="h-6 w-6 text-gray-400 dark:text-gray-500" />
              </div>
              <p className="text-base text-gray-600 dark:text-gray-400">No hay datos de reservas disponibles</p>
              <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">No se han registrado reservas en el período seleccionado</p>
            </div>
          )}
        </ComponentCard>

        {/* Lo más destacado */}
        <ComponentCard
          title="Lo más destacado"
          className="lg:col-span-2"
        >
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
                  <Award className="h-5 w-5 text-amber-500" />
                </div>
                <h4 className="text-base font-medium text-gray-800 dark:text-gray-200">
                  Excursión más vendida
                </h4>
              </div>
              <div className="pl-11">
                {stats.reservasPorMes.some(value => value > 0) && stats.excursionMasVendida ? (
                  <>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                      {stats.excursionMasVendida}
                    </p>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      {Math.max(...stats.reservasPorMes)} reservas en período seleccionado
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                      Sin datos
                    </p>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      No hay datos de reservas en este período
                    </p>
                  </>
                )}
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                  <Calendar className="h-5 w-5 text-green-500" />
                </div>
                <h4 className="text-base font-medium text-gray-800 dark:text-gray-200">
                  Temporada de mayor venta
                </h4>
              </div>
              <div className="pl-11">
                {stats.ingresosPorMes.some(value => value > 0) && stats.temporadaMayorVenta ? (
                  <>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                      {stats.temporadaMayorVenta}
                    </p>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      {formatCurrency(Math.max(...stats.ingresosPorMes))} en ventas totales
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                      Sin datos
                    </p>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      No hay datos de ventas en este período
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </ComponentCard>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ComponentCard
          title="Evolución de reservas mensuales"
          desc="Número de reservas por mes"
        >
          {stats.reservasPorMes.some(value => value > 0) ? (
            <div className="h-80">
              <BarChartOne series={barChartData} />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-80 text-center">
              <div className="mb-3 h-12 w-12 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                <Users className="h-6 w-6 text-gray-400 dark:text-gray-500" />
              </div>
              <p className="text-base text-gray-600 dark:text-gray-400">No hay datos de reservas mensuales</p>
              <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">No se han registrado reservas en el período seleccionado</p>
            </div>
          )}
        </ComponentCard>

        <ComponentCard
          title="Tendencia de ingresos"
          desc="Ingresos mensuales en USD"
        >
          {stats.ingresosPorMes.some(value => value > 0) ? (
            <div className="h-80">
              <LineChartOne series={lineChartData} />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-80 text-center">
              <div className="mb-3 h-12 w-12 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-gray-400 dark:text-gray-500" />
              </div>
              <p className="text-base text-gray-600 dark:text-gray-400">No hay datos de ingresos mensuales</p>
              <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">No se han registrado ventas en el período seleccionado</p>
            </div>
          )}
        </ComponentCard>
      </div>
    </div>
  );
} 